
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Search } from "lucide-react";
import { UserWithProfile, UserSession } from "@/types/analytics";

export default function UserActivityTracker() {
  const [searchTerm, setSearchTerm] = useState("");
  
  // Fetch all users and their recent sessions
  const { data, isLoading, error } = useQuery({
    queryKey: ['userSessions'],
    queryFn: async () => {
      // Get all users
      const { data: { users }, error: authError } = await supabase.auth.admin.listUsers();
      
      if (authError) throw authError;
      
      // Get all sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_sessions')
        .select('*')
        .order('login_time', { ascending: false });
      
      if (sessionsError) throw sessionsError;
      
      // Combine users with their sessions
      return users.map(user => ({
        ...user,
        sessions: sessions.filter(session => session.user_id === user.id)
      })) as UserWithProfile[];
    },
  });
  
  // Filter users based on search term
  const filteredUsers = data?.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">Error loading user activity: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Activity Tracker</CardTitle>
        <CardDescription>
          Monitor user sessions and login activity
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Login Duration</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Device Info</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers && filteredUsers.length > 0 ? (
                  filteredUsers.flatMap((user) => (
                    user.sessions && user.sessions.length > 0 ? (
                      user.sessions.map((session: UserSession) => (
                        <TableRow key={session.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {new Date(session.login_time).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {session.logout_time ? (
                              `${Math.round((new Date(session.logout_time).getTime() - 
                                new Date(session.login_time).getTime()) / 60000)} minutes`
                            ) : (
                              "Active"
                            )}
                          </TableCell>
                          <TableCell>
                            {session.location || "Unknown"}
                          </TableCell>
                          <TableCell>
                            <div className="text-xs">
                              {session.device_info ? (
                                <>
                                  <div>Platform: {session.device_info.platform}</div>
                                  <div>Screen: {session.device_info.screenWidth}x{session.device_info.screenHeight}</div>
                                  <div>Timezone: {session.device_info.timeZone}</div>
                                </>
                              ) : (
                                "Unknown"
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell colSpan={4} className="text-muted-foreground">
                          No sessions recorded
                        </TableCell>
                      </TableRow>
                    )
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
